<template>
  <div class="container">
    <el-card class="card" shadow="never">
      <div slot="header" class="card-header">
        <h2>{{ $t("touristRoutes") }}</h2>
      </div>
      <el-container>
        <!-- 导航菜单 -->
        <el-header>
          <div class="header-content">
            <Navbar
              :activeTab="activeStatus"
              @tab-change="handleStatusChange"
            />
          </div>
        </el-header>
        <el-main>
          <!-- 筛选条件 -->
          <el-card class="filter-card">
            <el-form
              :inline="true"
              :model="filterForm"
              class="demo-form-inline"
            >
              <el-form-item :label="$t('routeName')">
                <el-input
                  v-model="filterForm.search"
                  :placeholder="$t('searchRoutePlaceholder')"
                ></el-input>
              </el-form-item>
              <el-form-item>
                <el-button type="primary" @click="handleFilter">
                  {{ $t("filter") }}
                </el-button>
                <el-button @click="resetFilter">
                  {{ $t("reset") }}
                </el-button>
              </el-form-item>
            </el-form>
          </el-card>

          <!-- 旅游路线列表 -->
          <el-table
            :data="displayRoutes"
            stripe
            border
            style="width: 100%"
            v-loading="loading"
          >
            <el-table-column prop="image" :label="$t('coverImage')" width="120">
              <template slot-scope="scope">
                <img
                  :src="scope.row.cover_image"
                  :alt="$t('routeCover')"
                  class="table-image"
                />
              </template>
            </el-table-column>

            <el-table-column
              prop="name"
              :label="$t('routeName')"
              min-width="180"
            >
              <template slot-scope="scope">
                {{ scope.row.route_name }}
              </template>
            </el-table-column>

            <el-table-column
              prop="duration"
              :label="$t('tripDays')"
              width="100"
            >
              <template slot-scope="scope">
                {{ scope.row.days }}{{ $t("day") }}
              </template>
            </el-table-column>

            <el-table-column
              prop="departure_dates"
              :label="$t('travelDate')"
              width="100"
            >
              <template slot-scope="scope">
                {{ scope.row.departure_dates }}
              </template>
            </el-table-column>

            <el-table-column
              prop="from_place"
              :label="$t('departure')"
              width="120"
            >
              <template slot-scope="scope">
                {{ scope.row.departure }}
              </template>
            </el-table-column>

            <el-table-column
              prop="to_place"
              :label="$t('destination')"
              width="120"
            >
              <template slot-scope="scope">
                {{ scope.row.destination }}
              </template>
            </el-table-column>

            <el-table-column prop="price" :label="$t('price')" width="100">
              <template slot-scope="scope"> ¥{{ scope.row.price }} </template>
            </el-table-column>

            <el-table-column prop="status" :label="$t('status')" width="120">
              <template slot-scope="scope">
                <el-tag :type="getStatusType(scope.row.audit_status)">
                  {{
                    $i18n.locale === "en"
                      ? scope.row.audit_status_text_en
                      : scope.row.audit_status_text
                  }}
                </el-tag>
              </template>
            </el-table-column>

            <el-table-column :label="$t('operation')" width="320">
              <template slot-scope="scope">
                <el-button
                  size="mini"
                  type="primary"
                  @click="handleEdit(scope.row)"
                >
                  {{ $t("edit") }}
                </el-button>

                <el-button
                  size="mini"
                  :type="scope.row.status === 1 ? 'danger' : 'success'"
                  @click="handleToggleStatus(scope.row)"
                >
                  {{ scope.row.status === 1 ? $t("disable") : $t("enable") }}
                </el-button>

                <el-button
                  v-if="scope.row.audit_status === 2"
                  size="mini"
                  type="success"
                  @click="handleAddTravel(scope.row)"
                >
                  {{ $t("addTravelPlan") }}
                </el-button>
              </template>
            </el-table-column>
          </el-table>

          <!-- 分页控件 -->
          <Pagination
            :total="total"
            :page-size="pageSize"
            :current-page="currentPage"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </el-main>
      </el-container>
    </el-card>
  </div>
</template>

<script>
import Navbar from "../../components/Navbar.vue";
import Pagination from "../../components/Pagination.vue";
import { getTravelRoutes, toggleRouteStatus } from "../../api/travelRoute.js";

export default {
  components: {
    Navbar,
    Pagination,
  },
  data() {
    return {
      activeStatus: "all",
      routes: [],
      total: 0,
      loading: false,
      filterForm: {
        search: "",
        description: "",
        destination: "",
      },
      currentPage: 1,
      pageSize: 5,
    };
  },
  computed: {
    displayRoutes() {
      return this.routes;
    },
  },
  created() {
    this.loadRoutes();
  },
  methods: {
    async loadRoutes() {
      this.loading = true;
      try {
        const params = {
          audit_status: this.activeStatus,
          search: this.filterForm.search || this.filterForm.description,
          page: this.currentPage,
          page_size: this.pageSize,
        };
        const response = await getTravelRoutes(params);
        if (response.data.code === 1) {
          this.routes = response.data.data.list;
          this.total = response.data.data.total;
        } else {
          this.$message.error(
            response.data.msg || this.$t("getRouteListFailed"),
          );
        }
      } catch (error) {
        this.$message.error(this.$t("networkErrorGetRouteFailed"));
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    handleStatusChange(status) {
      this.activeStatus = status;
      this.currentPage = 1;
      this.loadRoutes();
    },
    handleFilter() {
      this.currentPage = 1;
      this.loadRoutes();
    },
    resetFilter() {
      this.filterForm = {
        search: "",
        description: "",
        destination: "",
      };
      this.currentPage = 1;
      this.loadRoutes();
    },
    handleCurrentChange(page) {
      this.currentPage = page;
      this.loadRoutes();
    },
    handleSizeChange(size) {
      this.pageSize = size;
      this.currentPage = 1;
      this.loadRoutes();
    },
    getStatusType(status) {
      switch (String(status)) {
        case "1":
          return "warning";
        case "2":
          return "success";
        case "3":
          return "danger";
        default:
          return "default";
      }
    },
    handleEdit(route) {
      // 1. 获取当前全局语言 & 本条路线主语言
      const currentLocale = this.$i18n.locale;
      const routeLang = route.language || "zh";

      // 2. 语言不一致则切换全局i18n语言
      if (currentLocale !== routeLang) {
        this.$i18n.locale = routeLang;
        // 多语言提示文案，可自行翻译
        const tipMap = {
          zh: "已自动切换为中文",
          en: "Automatically switched to English",
          ru: "Автоматически переключено на русский",
        };
        this.$message.info(tipMap[routeLang] || "语言已切换");
      }

      // 3. 跳转到编辑页面
      this.$router.push({
        path: "/travel/route-publish",
        query: { id: route.id },
      });
    },
    async handleToggleStatus(route) {
      const targetStatus = route.status === 1 ? 0 : 1;
      const actionText =
        targetStatus === 1 ? this.$t("enable") : this.$t("disable");
      this.$confirm(
        `${this.$t("confirm")}${actionText}${this.$t("thisRoute")}?`,
        this.$t("tip"),
        {
          confirmButtonText: this.$t("confirm"),
          cancelButtonText: this.$t("cancel"),
          type: "warning",
        },
      )
        .then(async () => {
          try {
            const response = await toggleRouteStatus(route.id, targetStatus);
            if (response.data.code === 1) {
              this.$message.success(`${actionText}${this.$t("success")}`);
              this.loadRoutes();
            } else {
              this.$message.error(
                response.data.msg || `${actionText}${this.$t("failed")}`,
              );
            }
          } catch (error) {
            this.$message.error(this.$t("networkErrorOperateFailed"));
            console.error(error);
          }
        })
        .catch(() => {
          this.$message.info(`${this.$t("canceled")}${actionText}`);
        });
    },
    handleAddTravel(route) {
      this.$router.push({
        path: "/travel/publish",
        query: {
          templateId: route.id,
        },
      });
    },
  },
};
</script>

<style scoped>
@import "@/assets/css/person-common.css";
.logo {
  font-size: 24px;
  font-weight: bold;
  margin-right: 20px;
}
.table-image {
  width: 100px;
  height: 60px;
  object-fit: cover;
}
@media (max-width: 768px) {
  ::v-deep .card > .el-card__body {
    padding: 0px !important;
  }
}
</style>
